import { Box, Typography } from '@mui/material';

import { caloTheme } from '../../assets/themes/calo';
import type { Kitchen } from '../../libs';
import type { Batch } from '../BatchTracking/types';
import { chefText, focusRing, tabular } from '../chefStyles';
import BatchCard from './BatchCard';
import { timeInKitchen } from './kitchenTime';
import TaskCard from './TaskCard';
import type { TimelineEntry } from './timeline';

const { palette } = caloTheme;

type HomeTimelineProps = {
  entries: TimelineEntry[];
  kitchen: Kitchen;
  now: Date;
  label: string;
  onOpen: (route: string) => void;
  onEditBatch: (batch: Batch) => void;
};

// Three columns: the time, the rail with its dots, the card. Dot and line positions are px strings: a bare number in `sx` is px or %.
const TIME_COLUMN = { xs: 48, md: 56 };
const RAIL_COLUMN = 16;
const COLUMN_GAP = { xs: 8, md: 12 };
// The centre of an entry's dot: the card's top padding, plus half the 28px first line.
const DOT_CENTRE = { xs: '30px', md: '34px' };
const NOW_DOT_CENTRE = '14px';
const LINE_LEFT = {
  xs: `${TIME_COLUMN.xs + COLUMN_GAP.xs + RAIL_COLUMN / 2 - 1}px`,
  md: `${TIME_COLUMN.md + COLUMN_GAP.md + RAIL_COLUMN / 2 - 1}px`
};

// The grid of one timeline row; the loading skeleton uses it too, so nothing jumps when the data arrives.
export const rowSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: `${TIME_COLUMN.xs}px ${RAIL_COLUMN}px minmax(0, 1fr)`,
    md: `${TIME_COLUMN.md}px ${RAIL_COLUMN}px minmax(0, 1fr)`
  },
  columnGap: { xs: 1, md: 1.5 },
  minWidth: 0
} as const;

const timeSx = { ...chefText.body, lineHeight: '28px', fontWeight: 700, whiteSpace: 'nowrap', ...tabular } as const;

type RailLineProps = { first: boolean; last: boolean; now: boolean };

// One piece of the rail per entry, running through the entry gap so it meets the next piece with no break.
const RailLine = ({ first, last, now }: RailLineProps) => {
  const centre = now ? { xs: NOW_DOT_CENTRE, md: NOW_DOT_CENTRE } : DOT_CENTRE;
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        left: LINE_LEFT,
        width: '2px',
        backgroundColor: palette.neutral300,
        top: first ? centre : 0,
        bottom: last ? { xs: `calc(100% - ${centre.xs})`, md: `calc(100% - ${centre.md})` } : 0
      }}
    />
  );
};

const HomeTimeline = ({ entries, kitchen, now, label, onOpen, onEditBatch }: HomeTimelineProps) => (
  <Box role="region" aria-label="Task timeline" tabIndex={0} sx={{ m: -0.5, p: 0.5, '&:focus-visible': focusRing }}>
    <Box component="ol" role="list" aria-label={label} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {entries.map((item, at) => {
        const task = item.entry === 'task' ? item.task : undefined;
        const batch = item.entry === 'batch' ? item.batch : undefined;
        const isNow = item.entry === 'now';
        const done = task?.doneAt !== undefined;
        const startsAt = task?.startsAt ?? batch?.createdAt ?? now.toISOString();
        const markerColor = done ? palette.neutral700 : palette.primary800;
        const last = at === entries.length - 1;
        return (
          <Box
            component="li"
            key={task?.id ?? batch?.id ?? 'now'}
            aria-current={isNow ? 'time' : undefined}
            sx={{
              position: 'relative',
              ...rowSx,
              pb: last ? 0 : { xs: 2, md: 3 }
            }}
          >
            {entries.length > 1 && <RailLine first={at === 0} last={last} now={isNow} />}
            <Typography
              component="time"
              dateTime={startsAt}
              sx={{
                ...timeSx,
                display: 'block',
                textAlign: 'right',
                pt: isNow ? 0 : { xs: '16px', md: '20px' },
                color: isNow ? palette.primary800 : palette.neutral900
              }}
            >
              {timeInKitchen(startsAt, kitchen)}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                aria-hidden="true"
                sx={
                  isNow
                    ? {
                        zIndex: 1,
                        width: '16px',
                        height: '16px',
                        mt: '6px',
                        flexShrink: 0,
                        boxSizing: 'border-box',
                        borderRadius: '50%',
                        border: `2px solid ${palette.primary800}`,
                        backgroundColor: palette.primary800,
                        boxShadow: `0 0 0 4px ${palette.primary100}`
                      }
                    : {
                        zIndex: 1,
                        width: '12px',
                        height: '12px',
                        mt: { xs: '24px', md: '28px' },
                        flexShrink: 0,
                        boxSizing: 'border-box',
                        borderRadius: '50%',
                        border: `2px solid ${markerColor}`,
                        backgroundColor: done ? markerColor : palette.white
                      }
                }
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {isNow && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 28 }}>
                  <Typography sx={{ ...timeSx, color: palette.primary800 }}>Now</Typography>
                  <Box aria-hidden="true" sx={{ flex: 1, height: '2px', backgroundColor: palette.primary800 }} />
                </Box>
              )}
              {task !== undefined && (
                <Box sx={{ overflowWrap: 'anywhere' }}>
                  <TaskCard task={task} kitchen={kitchen} onOpen={onOpen} />
                </Box>
              )}
              {batch !== undefined && (
                <Box sx={{ overflowWrap: 'anywhere' }}>
                  <BatchCard batch={batch} now={now} onEdit={() => onEditBatch(batch)} />
                </Box>
              )}
              {task?.doneAt !== undefined && (
                <Typography sx={{ mt: 1, ...chefText.meta, fontWeight: 700, color: palette.neutral800, ...tabular }}>
                  Done {timeInKitchen(task.doneAt, kitchen)}
                </Typography>
              )}
              {item.entry === 'task' && item.gap !== undefined && (
                <Typography sx={{ mt: done ? 0.5 : 1, ...chefText.meta, color: palette.neutral800, ...tabular }}>
                  {item.gap}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

export default HomeTimeline;
